import {
  DeleteOutlined,
  EditOutlined,
  RightOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Card, message, Popconfirm, Tag, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useMemo, useState } from 'react';

import { DATE, isWeekend, monthGrid, monthName, WEEKDAYS } from '../Calendar/types';
import { queryErrorMessage } from '../CashTransactions/types';
import { type ClubMonth, datesOnWeekdays, formatRange, type ScheduleClub } from './types';

interface Props {
  club: ScheduleClub;
  month: ClubMonth;
  holidays: Map<string, string>;
  canEdit: boolean;
  onEdit: () => void;
  onChanged: () => void;
}

// One Club month: a calendar of its dates where its Sessions are marked.
// Editors click dates on and off (or fill them from the club's weekdays) and
// save; Holidays are flagged. Starts collapsed (only its header shows); the
// header expands and collapses it.
export default function MonthCard({ club, month, holidays, canEdit, onEdit, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const { runQuery } = useScreen();
  const [dates, setDates] = useState<Set<string>>(() => new Set(month.dates));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => setDates(new Set(month.dates)), [month.dates]);

  const dirty = dates.size !== month.dates.length || month.dates.some((d) => !dates.has(d));

  // The calendar months the Club month touches.
  const calendars = useMemo(() => {
    const list: Dayjs[] = [];
    const last = dayjs(month.end_date).startOf('month');
    for (
      let m = dayjs(month.start_date).startOf('month');
      !m.isAfter(last);
      m = m.add(1, 'month')
    ) {
      list.push(m);
    }
    return list;
  }, [month.start_date, month.end_date]);

  const toggle = (date: string) =>
    setDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  const fill = () =>
    setDates(
      new Set([
        ...dates,
        ...datesOnWeekdays(
          month.start_date,
          month.end_date,
          club.week_days,
          new Set(holidays.keys()),
        ),
      ]),
    );

  const save = async () => {
    setSaving(true);
    try {
      await runQuery('club_sessions_set', { monthId: month.id, dates: [...dates].sort() });
      message.success('Хадгаллаа');
      onChanged();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await runQuery('club_month_delete', { id: month.id });
      message.success('Устгалаа');
      onChanged();
    } catch (e) {
      message.error(queryErrorMessage(e));
      setDeleting(false);
    }
  };

  const onHoliday = [...dates].filter((d) => holidays.has(d)).length;

  return (
    <Card
      size="small"
      styles={{ body: open ? undefined : { display: 'none' } }}
      title={
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 border-0 bg-transparent py-1 text-left"
        >
          <RightOutlined
            className={`text-xs text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <span className="text-base font-semibold">{month.name}</span>
          <span className="text-xs font-normal text-gray-500">
            {formatRange(month.start_date, month.end_date)}
          </span>
          <Tag color={dates.size ? 'blue' : 'default'} bordered={false}>
            {dates.size} өдөр
          </Tag>
          {onHoliday > 0 && (
            <Tag color="red" bordered={false}>
              Амралтын өдөрт: {onHoliday}
            </Tag>
          )}
          {dirty && (
            <Tag color="orange" bordered={false}>
              Хадгалаагүй
            </Tag>
          )}
        </button>
      }
      extra={
        canEdit && (
          <div className="flex gap-1">
            <Tooltip title="Засах">
              <Button type="text" icon={<EditOutlined />} onClick={onEdit} />
            </Tooltip>
            <Popconfirm
              title="Сар устгах уу?"
              description={
                month.dates.length
                  ? `"${month.name}" ба түүний ${month.dates.length} хичээллэх өдөр устна.`
                  : `"${month.name}"`
              }
              okText="Устгах"
              cancelText="Болих"
              okButtonProps={{ danger: true }}
              onConfirm={remove}
            >
              <Tooltip title="Устгах">
                <Button type="text" danger icon={<DeleteOutlined />} loading={deleting} />
              </Tooltip>
            </Popconfirm>
          </div>
        )
      }
    >
      <div className="flex flex-wrap gap-6">
        {calendars.map((cal) => (
          <MiniMonth
            key={cal.format('YYYY-MM')}
            month={cal}
            from={month.start_date}
            to={month.end_date}
            dates={dates}
            holidays={holidays}
            canEdit={canEdit}
            onToggle={toggle}
          />
        ))}
      </div>

      {canEdit && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <Tooltip
            title={
              club.week_days.length
                ? 'Дугуйлангийн гарагуудаар (амралтын өдрийг алгасна)'
                : 'Дугуйланд гараг сонгоогүй байна'
            }
          >
            <Button icon={<ThunderboltOutlined />} disabled={!club.week_days.length} onClick={fill}>
              Гарагаар бөглөх
            </Button>
          </Tooltip>
          <Button disabled={dates.size === 0} onClick={() => setDates(new Set())}>
            Цэвэрлэх
          </Button>
          <div className="ml-auto flex gap-2">
            {dirty && <Button onClick={() => setDates(new Set(month.dates))}>Болих</Button>}
            <Button type="primary" disabled={!dirty} loading={saving} onClick={save}>
              Хадгалах
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

interface MiniMonthProps {
  month: Dayjs;
  from: string;
  to: string;
  dates: Set<string>;
  holidays: Map<string, string>;
  canEdit: boolean;
  onToggle: (date: string) => void;
}

// One calendar month; only the Club month's days can be picked.
function MiniMonth({ month, from, to, dates, holidays, canEdit, onToggle }: MiniMonthProps) {
  const today = dayjs().format(DATE);
  return (
    <div className="w-[252px]">
      <div className="mb-2 text-sm font-semibold text-gray-700">
        {month.year()} оны {monthName(month)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-[11px] font-medium text-gray-400">
            {d}
          </div>
        ))}
        {monthGrid(month)
          .flat()
          .map((day) => {
            const date = day.format(DATE);
            if (day.month() !== month.month()) return <div key={date} className="h-8" />;
            const inRange = from <= date && date <= to;
            const selected = dates.has(date);
            const holiday = holidays.get(date);
            const look = `relative flex h-8 items-center justify-center border text-xs transition ${
              !inRange
                ? 'border-transparent bg-transparent text-gray-300'
                : selected
                  ? 'border-blue-600 bg-blue-600 font-semibold text-white'
                  : `border-gray-100 bg-white ${isWeekend(day) ? 'text-gray-400' : 'text-gray-700'}`
            } ${date === today && !selected ? 'ring-1 ring-blue-400' : ''}`;
            const content = (
              <>
                {day.date()}
                {holiday && inRange && (
                  <span
                    className={`absolute top-0.5 right-0.5 size-1.5 ${selected ? 'bg-white' : 'bg-red-500'}`}
                  />
                )}
              </>
            );
            // Only editors pick days, and only within the Club month.
            const cell =
              inRange && canEdit ? (
                <button
                  key={date}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onToggle(date)}
                  className={`${look} cursor-pointer ${selected ? '' : 'hover:border-blue-400 hover:text-blue-600'}`}
                >
                  {content}
                </button>
              ) : (
                <div key={date} className={look}>
                  {content}
                </div>
              );
            return holiday && inRange ? (
              <Tooltip key={date} title={`Амралт: ${holiday}`}>
                {cell}
              </Tooltip>
            ) : (
              cell
            );
          })}
      </div>
    </div>
  );
}
